import { service } from '@ember/service';
import { trackedFunction } from 'reactiveweb/function';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import type { Company as CompanyModel } from 'tio-data/data/resources/company.type';
import type { TasProgramTemplateExtension as TasProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';
import type {
  TasProgramInstance,
  AggregatedProgramInstance,
  OpenSearchResponse,
  ProgramsSearchResult,
} from 'tio-data/data/resources/search.ext';
import { hash, array } from '@ember/helper';
import { OpenSearchQueryBuilder } from 'tio-common/utils/open-search-builder';
import { OpenSearchResponseBuilder } from 'tio-common/utils/open-search-response-builder';
import { downloadSearchCsv, executeSearchRequest } from 'tio-data/requests/search';
import type FeaturesService from 'tio-common/services/features';

import { or } from 'tio-ui/utilities';
import {
  getModelTypeFromDataset,
  getDefaultSortFields,
  getEssentialFields,
  getCompanyFilterField,
  AVAILABLE_DATASETS,
} from '../../utils/dataset-utils';
import { RELATIVE_DATE_PRESETS } from 'tio-common/components/forms/relative-date-range-picker';
import type { ApplicationFilterSignature, PaymentFilterSignature } from './types.gts';
import type { ChartDataPoint } from 'tio-common/types/chart';
import type { RelativeDatePreset } from 'tio-common/components/forms/relative-date-range-picker';
import {
  employeeCustomFieldValue,
  employeeCustomLeafFallbackKey,
} from '../../utils/employee-custom-field-resolver';
import { tasCustomFieldValue } from '../../utils/tas-custom-field-resolver';

interface ProgramInstanceAggregationResult {
  programInstanceId: string;
  employeeId: string;
  employeeName?: string;
  totalApplications: number;
  applicationStateCounts: Record<string, number>;
  sampleDocument?: unknown;
}

interface TopHitsAggregationResult {
  hits?: {
    hits?: Array<{ _source?: TasProgramInstance }>;
  };
}

interface FilterWithTopHitAggregationResult {
  docCount?: number;
  firstHit?: TopHitsAggregationResult;
}

interface ChartData {
  field: string;
  dataPoints: ChartDataPoint[];
  total: number;
}

const DEFAULT_DATASET = 'tas-program-instances';
const VALID_REPORT_DATASET_KEYS = new Set(AVAILABLE_DATASETS.map((dataset) => dataset.key));

const AGGREGATE_TOP_HIT_SORT = [
  { tas_program_instance_created_at_date: { order: 'desc' as const } },
  { tas_application_created_at_date: { order: 'desc' as const } },
];

export interface ProgramsDataProviderSignature {
  Args: ApplicationFilterSignature &
    PaymentFilterSignature & {
      company?: CompanyModel;
      limit: number;
      page: number;

      // Dataset selection (for multi-dataset support in analytics)
      selectedDataset?: string; // e.g., 'tas-program-instances', 'people', 'pslf-forms'

      // Program filters
      programTemplateId?: string;
      programCreatedAtStart?: string;
      programCreatedAtEnd?: string;
      excludeNonVoucherPrograms?: boolean;
      courseCashProgramsOnly?: boolean;

      // Course filters
      includeCourses?: boolean;
      coursesBeginDate?: string;
      coursesEndDate?: string;

      // Approver filters
      approverId?: string;

      // Employee filters
      employeeId?: string;
      terminatedEmployees?: boolean;
      excludeTermVerified?: boolean;

      // Invoice filters
      includeInvoiceDate?: boolean;

      // Column selection
      selectedColumns?: string[];

      // Sort field (camelCase with :asc/:desc suffix, e.g. 'companyNameKeyword:asc')
      sortField?: string;

      // Generic filters for non-TAS datasets (field key → value, with __gte/__lte for ranges)
      genericFilters?: Record<string, unknown>;

      // Whether the caller has finished hydrating field metadata.
      // When false, search is deferred to avoid startup request churn.
      isReady?: boolean;

      // Program template metadata supplied by parent (avoids duplicate query)
      programTemplates?: TasProgramTemplateModel[];
      isLoadingProgramTemplates?: boolean;
      selectedProgramTemplate?: TasProgramTemplateModel;

      // Callback for updating query parameters
      updateQueryParameters: (params: Record<string, unknown>) => void;

      // Notified once per distinct response when the API echoes back the
      // canonical _source paths it recognized for the request. Parents use
      // this to prune selectedColumns whose canonical key the backend
      // could not resolve, preventing the column from rendering blank.
      onResolvedSource?: (resolvedSource: string[]) => void;
    };
  Blocks: {
    default: [
      {
        // Data and loading states
        data: ProgramsSearchResult;
        isLoading: boolean;
        isDataLoaded: boolean;
        error?: Error;

        // Program templates for filters
        programTemplates: TasProgramTemplateModel[];
        isLoadingProgramTemplates: boolean;
        selectedProgramTemplate?: TasProgramTemplateModel;

        // Actions
        downloadCsv: () => Promise<unknown>;
        refresh: () => void;
        updateQueryParameters: (params: Record<string, unknown>) => void;

        // Chart-specific functionality
        buildChartData: (field: string) => ChartData | null;
        getAvailableChartFields: () => Array<{ key: string; label: string }>;

        // Utility getters
        totalRecords: number;
        executionTime: number;

        // Canonical _source paths the backend recognized for the most
        // recent response, or undefined when the response did not include
        // a _source filter (or the API does not yet emit fieldMeta).
        resolvedSource?: string[];
      },
    ];
  };
}

export default class ProgramsDataProviderComponent extends Component<ProgramsDataProviderSignature> {
  @service declare store: Store;
  // WarpDrive wave 1.5: features.ENV.apiKey feeds the raw-fetch CSV download
  // helper (tio-data is owner-free, so the per-app api key is passed in).
  @service declare features: FeaturesService;

  private lastSearchRequestKey?: string;
  private lastSearchRequestPromise?: Promise<
    OpenSearchResponse<TasProgramInstance | Record<string, unknown>>
  >;

  private isTasCustomFlattenedColumn(columnKey: string): boolean {
    return (
      columnKey.startsWith('tas_program_instance_custom_fields_flattened.') ||
      columnKey.startsWith('tas_applicant_custom_fields_flattened.') ||
      columnKey.startsWith('tas_application_custom_fields_flattened.') ||
      columnKey.startsWith('tas_course_custom_fields_flattened.') ||
      columnKey.startsWith('tas_limit_custom_fields_flattened.') ||
      columnKey.startsWith('tasProgramInstanceCustomFieldsFlattened.') ||
      columnKey.startsWith('tasApplicantCustomFieldsFlattened.') ||
      columnKey.startsWith('tasApplicationCustomFieldsFlattened.') ||
      columnKey.startsWith('tasCourseCustomFieldsFlattened.') ||
      columnKey.startsWith('tasLimitCustomFieldsFlattened.')
    );
  }

  private aggregateFallbackCustomColumns(selectedColumns: string[] = []): string[] {
    return [
      ...new Set(selectedColumns.filter((column) => this.isTasCustomFlattenedColumn(column))),
    ];
  }

  private aggregateFallbackAggregationName(index: number): string {
    return `firstNonEmptyCustomField${index}`;
  }

  private resolveModelType(dataset: string): string {
    if (dataset === 'tas-program-instances' && this.args.showIndividualApplications) {
      return 'TasApplication';
    }

    return getModelTypeFromDataset(dataset);
  }

  private getNestedValue(record: Record<string, unknown>, path: string): unknown {
    return path
      .split('.')
      .filter(Boolean)
      .reduce<unknown>((acc, segment) => {
        if (!acc || typeof acc !== 'object') {
          return undefined;
        }

        return (acc as Record<string, unknown>)[segment];
      }, record);
  }

  private setNestedValue(record: Record<string, unknown>, path: string, value: unknown): void {
    const segments = path.split('.').filter(Boolean);
    if (segments.length === 0) {
      return;
    }

    let current: Record<string, unknown> = record;
    segments.slice(0, -1).forEach((segment) => {
      const existing = current[segment];
      if (!existing || typeof existing !== 'object' || Array.isArray(existing)) {
        current[segment] = {};
      }

      current = current[segment] as Record<string, unknown>;
    });

    const leaf = segments.at(-1);
    if (!leaf) {
      return;
    }

    current[leaf] = value;
  }

  private hasMeaningfulAggregateValue(value: unknown): boolean {
    if (value === undefined || value === null) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return true;
  }

  private mergeAggregateFallbackField(
    target: Record<string, unknown>,
    sourceDocument: TasProgramInstance,
    columnKey: string
  ): void {
    const existingValue = this.resolveAggregateColumnValue(target, columnKey);
    if (this.hasMeaningfulAggregateValue(existingValue)) {
      return;
    }

    const sourceValue = this.resolveAggregateColumnValue(
      sourceDocument as Record<string, unknown>,
      columnKey
    );
    if (!this.hasMeaningfulAggregateValue(sourceValue)) {
      return;
    }

    this.setNestedValue(target, columnKey, sourceValue);
  }

  private resolveAggregateColumnValue(record: Record<string, unknown>, columnKey: string): unknown {
    const directValue = this.getNestedValue(record, columnKey);
    if (this.hasMeaningfulAggregateValue(directValue)) {
      return directValue;
    }

    if (
      columnKey.startsWith('employeeEfFields.') &&
      !columnKey.startsWith('employeeEfFields.employeeCustomFields.')
    ) {
      const leaf = columnKey.split('.').filter(Boolean).at(-1);
      if (leaf) {
        const employeeEf =
          (record.employeeEfFields as Record<string, unknown> | undefined) ||
          (record['employee_ef_fields'] as Record<string, unknown> | undefined);

        const directEfValue = employeeCustomFieldValue(employeeEf, leaf);
        if (this.hasMeaningfulAggregateValue(directEfValue)) {
          return directEfValue;
        }

        const customFields =
          (employeeEf?.employeeCustomFields as Record<string, unknown> | undefined) ||
          (employeeEf?.employee_custom_fields as Record<string, unknown> | undefined);
        const nestedEfValue = employeeCustomFieldValue(customFields, leaf);
        if (this.hasMeaningfulAggregateValue(nestedEfValue)) {
          return nestedEfValue;
        }
      }
    }

    const tasValue = tasCustomFieldValue(record, columnKey);
    if (this.hasMeaningfulAggregateValue(tasValue)) {
      return tasValue;
    }

    return directValue;
  }

  private extractTopHitSource(
    aggregation?: TopHitsAggregationResult
  ): TasProgramInstance | undefined {
    return aggregation?.hits?.hits?.[0]?._source;
  }

  private get selectedDatasetKey(): string {
    const requestedDataset = this.args.selectedDataset?.trim();
    if (!requestedDataset) {
      return DEFAULT_DATASET;
    }

    return VALID_REPORT_DATASET_KEYS.has(requestedDataset) ? requestedDataset : DEFAULT_DATASET;
  }

  // Transform program-instance aggregation logic from OpenSearchResponseBuilder
  private transformProgramInstanceAggregation(
    responseBuilder: OpenSearchResponseBuilder<TasProgramInstance>
  ): ProgramInstanceAggregationResult[] {
    const employees = responseBuilder.getTermsAggregation('employees');
    const fallbackCustomColumns = this.aggregateFallbackCustomColumns(
      this.args.selectedColumns || []
    );

    return employees.map((employeeBucket) => {
      // Get application state counts - API returns camelized field names
      const applicationStates = employeeBucket.aggregations?.applicationStates as
        { buckets?: Array<Record<string, unknown>> } | undefined;
      const stateCounts: Record<string, number> = {};

      if (applicationStates?.buckets) {
        applicationStates.buckets.forEach((stateBucket: Record<string, unknown>) => {
          stateCounts[String(stateBucket.key)] = stateBucket.docCount as number;
        });
      }

      // Get sample document - API returns camelized field names in _source
      let sampleDoc = this.extractTopHitSource(
        employeeBucket.aggregations?.sampleDocument as TopHitsAggregationResult | undefined
      );

      if (fallbackCustomColumns.length > 0) {
        const mergedSampleDoc = {
          ...((sampleDoc as Record<string, unknown> | undefined) || {}),
        } as Record<string, unknown>;

        fallbackCustomColumns.forEach((columnKey, index) => {
          const fallbackAggregation = employeeBucket.aggregations?.[
            this.aggregateFallbackAggregationName(index)
          ] as FilterWithTopHitAggregationResult | undefined;
          const fallbackDocument = this.extractTopHitSource(fallbackAggregation?.firstHit);

          if (fallbackDocument) {
            this.mergeAggregateFallbackField(mergedSampleDoc, fallbackDocument, columnKey);
          }
        });

        if (Object.keys(mergedSampleDoc).length > 0) {
          sampleDoc = mergedSampleDoc as TasProgramInstance;
        }
      }

      return {
        programInstanceId: sampleDoc?.tasProgramInstanceIdKeyword || employeeBucket.key,
        employeeId: sampleDoc?.employeeIdKeyword || '',
        employeeName: sampleDoc?.employeeNameKeyword,
        totalApplications: employeeBucket.count,
        applicationStateCounts: stateCounts,
        sampleDocument: sampleDoc,
      };
    });
  }

  get results(): ProgramsSearchResult {
    const response = this.searchData.value;
    const isLoading = this.searchData.isLoading;
    if (!response) {
      return { items: [], total: 0, took: 0, isLoading };
    }

    const responseBuilder = OpenSearchResponseBuilder.from(response);

    const dataset = this.selectedDatasetKey;

    // BACKEND-FLATTENING CONTRACT:
    // Documents are consumed here directly via `responseBuilder.getDocuments()`
    // without any client-side normalization. This intentionally relies on the
    // tio-api search service to have already flattened nested custom-field
    // containers (e.g. `tas_application_fields`,
    // `all_employee_custom_fields_flattened`,
    // `tas_program_instance_custom_fields_flattened`) into top-level
    // snake_case keys on each hit's `_source`. If the backend regresses and
    // returns these as nested arrays again, the dev-mode canary in
    // `searchAdapter.search().then(...)` above will log a warning, and
    // affected report columns will render empty until backend flattening is
    // restored. See PR #7344 / #7570 for the full contract.
    if (this.args.showIndividualApplications || dataset !== 'tas-program-instances') {
      // Individual/document mode - extract documents from hits and return SearchModelWith format
      const items = responseBuilder.getDocuments() as Array<Record<string, unknown>>;
      return {
        items,
        total: responseBuilder.getTotalCount(),
        took: responseBuilder.getExecutionTime(),
        isLoading,
      };
    } else {
      // Aggregated mode - use our local transformer for employee aggregations
      // Type assertion is safe: aggregated mode is only used for TAS datasets
      const employeeResults = this.transformProgramInstanceAggregation(
        responseBuilder as OpenSearchResponseBuilder<TasProgramInstance>
      );

      // Map to the AggregatedProgramInstance format expected by the component
      const allItems: AggregatedProgramInstance[] = employeeResults.map((result) => {
        const sampleDoc = ((result.sampleDocument as TasProgramInstance | undefined) ??
          {}) as Record<string, unknown>;
        const programInstanceId = result.programInstanceId || `unknown-${result.employeeId}`;
        return {
          // NOTE: sampleDoc spread may overwrite explicitly-set keys below if they
          // exist in the OpenSearch _source. Consider spreading it last or picking
          // only the keys that are actually needed.
          ...sampleDoc,
          employeeId: result.employeeId,
          // API returns camelCase field names in response
          employeeNameKeyword:
            (result.sampleDocument as TasProgramInstance)?.employeeNameKeyword ||
            `Employee ${result.employeeId || 'unknown'}`,
          employeePayrollIdKeyword:
            (result.sampleDocument as TasProgramInstance)?.employeePayrollIdKeyword || '',
          programInstanceId,
          tasProgramInstanceIdKeyword: programInstanceId, // Canonical field name for column mapping
          tasProgramInstanceCreatedAtDate:
            (result.sampleDocument as TasProgramInstance)?.tasProgramInstanceCreatedAtDate || '',
          // Program name from template (correct field)
          tasProgramTemplateFieldProgramNameKeyword:
            (result.sampleDocument as TasProgramInstance)
              ?.tasProgramTemplateFieldProgramNameKeyword || 'Unknown Program',
          // DEPRECATED: Alias for backward compatibility with old column selections
          // Users may have selected this field, but the correct field is tasProgramTemplateFieldProgramNameKeyword
          tasProgramInstanceFieldNameKeyword:
            (result.sampleDocument as TasProgramInstance)
              ?.tasProgramTemplateFieldProgramNameKeyword ||
            (result.sampleDocument as TasProgramInstance)?.tasProgramInstanceFieldNameKeyword ||
            'Unknown Program',
          tasProgramInstanceFieldSchoolInstitutionNameKeyword:
            (result.sampleDocument as TasProgramInstance)
              ?.tasProgramInstanceFieldSchoolInstitutionNameKeyword || '',
          applicationStateCounts: result.applicationStateCounts,
          totalApplications: result.totalApplications,
        };
      });

      // Apply client-side pagination to the aggregated results
      const { page = 1, limit = 25 } = this.args;
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedItems = allItems.slice(startIndex, endIndex);

      return {
        items: paginatedItems,
        total: allItems.length, // Total number of unique employees (aggregation buckets)
        took: responseBuilder.getExecutionTime(),
        isLoading,
      };
    }
  }

  /**
   * Calculate actual date range based on relative preset
   * Returns the dates to use for the query (recalculated if preset is active)
   */
  getApplicationDateRange(): { start?: string; end?: string } {
    // If a relative preset is selected (not 'custom'), recalculate the dates
    const presetId = this.args.applicationDateRangePreset;

    if (presetId && presetId !== 'custom') {
      const preset = RELATIVE_DATE_PRESETS.find((p: RelativeDatePreset) => p.id === presetId);
      if (preset) {
        const { start, end } = preset.getDates();
        return { start, end };
      }
    }

    // Otherwise, use the stored dates as-is (custom mode or no preset)
    return {
      start: this.args.applicationCreatedAtStart,
      end: this.args.applicationCreatedAtEnd,
    };
  }

  buildSearchQuery() {
    const dataset = this.selectedDatasetKey;

    if (dataset !== 'tas-program-instances') {
      const companyFilterField = getCompanyFilterField(dataset);
      if (!companyFilterField) {
        throw new Error(`Missing company filter field for dataset: ${dataset}`);
      }

      const builder = new OpenSearchQueryBuilder();
      builder.setHighlight([]);
      builder.mustTerm(companyFilterField, this.args.company?.id);

      // Apply generic filters from the DatasetFilters component
      const filters = this.args.genericFilters;
      if (filters) {
        // Collect range filter fields (keys ending in __gte or __lte)
        const rangeFields = new Map<string, { gte?: string; lte?: string }>();

        for (const [key, value] of Object.entries(filters)) {
          if (value === undefined || value === null || value === '') continue;

          if (key.endsWith('__gte') || key.endsWith('__lte')) {
            // Range filter — extract field name and bound
            const fieldKey = key.replace(/__(?:gte|lte)$/, '');
            if (!rangeFields.has(fieldKey)) {
              rangeFields.set(fieldKey, {});
            }
            const range = rangeFields.get(fieldKey)!;
            if (key.endsWith('__gte')) {
              range.gte = String(value);
            } else {
              range.lte = String(value);
            }
          } else if (typeof value === 'boolean') {
            builder.mustTerm(key, value);
          } else if (Array.isArray(value) && value.length > 0) {
            builder.mustTerms(key, value);
          } else if (typeof value === 'string') {
            builder.mustTerm(key, value);
          }
        }

        // Apply collected range filters
        for (const [field, bounds] of rangeFields) {
          builder.range(field, bounds.gte, bounds.lte);
        }
      }

      return builder;
    }

    // Get the actual date range (recalculated if using a relative preset)
    const applicationDateRange = this.getApplicationDateRange();

    return new OpenSearchQueryBuilder()
      .setHighlight([])
      .mustTerm('company_id', this.args.company?.id)
      .mustTerm('tas_program_template_id_keyword', this.args.programTemplateId)
      .when(this.args.excludeNonVoucherPrograms, (b) => {
        b.mustTerm('tas_program_template_field_voucher_program_boolean', true);
      })
      .when(this.args.courseCashProgramsOnly, (b) => {
        b.addFilter({
          range: {
            tas_application_course_cash_reserved_amount_integer: { gt: 0 },
          },
        });
      })
      .range(
        'tas_program_instance_created_at_date',
        this.args.programCreatedAtStart,
        this.args.programCreatedAtEnd
      )
      .range(
        'tas_application_created_at_date',
        applicationDateRange.start,
        applicationDateRange.end
      )
      .when(this.args.coursesBeginDate || this.args.coursesEndDate, (b) => {
        // coursesBeginDate and coursesEndDate define a date range for filtering courses
        // that both START and END within the specified date range
        // e.g., "Show courses where begin date >= Oct 13 AND begin date <= Oct 14 AND end date <= Oct 14"
        b.exists('tas_application_field_courses_begin_date_date')
          .range(
            'tas_application_field_courses_begin_date_date',
            this.args.coursesBeginDate,
            this.args.coursesEndDate
          )
          .exists('tas_application_field_courses_end_date_date')
          .range(
            'tas_application_field_courses_end_date_date',
            undefined,
            this.args.coursesEndDate
          );
      })
      .mustTerm('employee_id', this.args.employeeId)
      .mustTerm('employment_termination_verified', this.args.excludeTermVerified)
      .mustTerm('tas_application_possible_reviewers', this.args.approverId)
      .when(this.args.terminatedEmployees, (b) => {
        b.range('employment_end_date', undefined, new Date().toISOString().split('T')[0]);
      })
      .mustTermFlexible(
        'tas_application_state_keyword',
        this.args.applicationStates?.split(',').filter(Boolean)
      )
      .when(this.args.paymentStates, (b) => {
        const paymentStateGroups = this.args.paymentStates?.split(',').map((state) => {
          const field = state.replace(/-/g, '_');
          return [
            { exists: { field } },
            {
              range: {
                [field]: {
                  [this.args.excludeNonVoucherPrograms ? 'gte' : 'gt']: 0,
                },
              },
            },
          ];
        });
        if (!paymentStateGroups || !paymentStateGroups.length) {
          return;
        }
        b.shouldEach(paymentStateGroups).setMinimumShouldMatch(1);
      })
      .when(this.args.paymentYear, (b) => {
        const year = Number(this.args.paymentYear);
        b.range('tas_application_payment_last_date_date', `${year}-01-01`, `${year + 1}-01-01`);
      });
  }

  private selectedSourceColumns(selectedColumns: string[]): string[] {
    // Column keys arrive as snake_case paths from the /fields API (e.g. employee_name_keyword)
    const snakeCaseColumns = selectedColumns;

    const selectedEmployeeEfLeaves = snakeCaseColumns
      .filter((column) => /^employee_ef_fields\.[^.]+$/.test(column))
      .map((column) => column.replace('employee_ef_fields.', ''));

    const employeeEfCustomNestedFields = selectedEmployeeEfLeaves.flatMap((leaf) => {
      const strippedLeaf = employeeCustomLeafFallbackKey(leaf);

      return Array.from(
        new Set([
          `employee_ef_fields.employee_custom_fields.${leaf}`,
          `employee_ef_fields.employee_custom_fields.${strippedLeaf}`,
        ])
      );
    });

    const includeEmployeeCustomFieldsContainer =
      selectedEmployeeEfLeaves.length > 0 ? ['employee_ef_fields.employee_custom_fields.*'] : [];

    const tasCustomFlattenedContainers = snakeCaseColumns
      .filter(
        (column) =>
          column.startsWith('tas_program_instance_custom_fields_flattened.') ||
          column.startsWith('tas_applicant_custom_fields_flattened.') ||
          column.startsWith('tas_application_custom_fields_flattened.') ||
          column.startsWith('tas_course_custom_fields_flattened.') ||
          column.startsWith('tas_limit_custom_fields_flattened.')
      )
      .map((column) => {
        const dotIndex = column.indexOf('.');
        const container = dotIndex >= 0 ? column.slice(0, dotIndex) : column;
        return `${container}.*`;
      });

    // Course object fields live in a nested array — request the whole container
    const courseObjectContainer = snakeCaseColumns.some((col) =>
      col.startsWith('tas_application_courses_object.')
    )
      ? ['tas_application_courses_object.*']
      : [];

    return [
      ...new Set([
        ...snakeCaseColumns,
        ...employeeEfCustomNestedFields,
        ...includeEmployeeCustomFieldsContainer,
        ...tasCustomFlattenedContainers,
        ...courseObjectContainer,
      ]),
    ];
  }

  private buildRawOpenSearchQuery() {
    const baseQuery = this.buildSearchQuery();
    const dataset = this.selectedDatasetKey;

    if (this.args.showIndividualApplications || dataset !== 'tas-program-instances') {
      // Individual/document mode - apply pagination to the OpenSearch query
      const { page = 1, limit = 25 } = this.args;

      // Sort field is already snake_case from the /fields API (e.g. employee_name_keyword:asc)
      let sortFields: string[];
      if (this.args.sortField) {
        sortFields = [this.args.sortField];
      } else {
        sortFields = getDefaultSortFields(dataset);
      }
      baseQuery.setPagination(page, limit).setSort(sortFields);

      if (this.args.selectedColumns && this.args.selectedColumns.length > 0) {
        const essentialFields = getEssentialFields(dataset);
        baseQuery.selectSourceFields(
          this.selectedSourceColumns(this.args.selectedColumns),
          essentialFields
        );
      }

      const query = baseQuery.build();

      return {
        query,
        aggs: {},
      };
    } else {
      // Aggregated mode - use the query builder's aggregation methods
      // Note: In aggregation mode, we need to fetch ALL buckets to get accurate total count
      // We'll handle client-side pagination by slicing the results array
      const aggregatedQuery = baseQuery
        .setPagination(0, 0) // Set size to 0 for aggregation-only queries (we only want aggregations)
        .addTermsAggregation('employees', 'tas_program_instance_id_keyword', 10000) // Fetch all program-instance buckets
        .addNestedAggregation('employees', 'application_states', {
          terms: {
            field: 'tas_application_state_keyword',
            size: 50,
          },
        });

      // Determine fields for sample document in aggregation
      // Use snake_case for OpenSearch field names in queries
      let sampleDocumentFields = [
        'employee_id_keyword',
        'employee_name_keyword',
        'employee_payroll_id_keyword',
        'tas_program_instance_id_keyword',
        'tas_program_instance_created_at_date',
        'tas_program_template_field_program_name_keyword',
        'tas_program_instance_field_school_institution_name_keyword',
      ];

      // If specific columns are selected, use those plus essential aggregation fields
      if (this.args.selectedColumns && this.args.selectedColumns.length > 0) {
        const snakeCaseColumns = this.selectedSourceColumns(this.args.selectedColumns);
        const fallbackCustomColumns = this.aggregateFallbackCustomColumns(
          this.args.selectedColumns
        );

        const essentialAggregationFields = [
          // Core fields needed for aggregation display
          'employee_id_keyword',
          'employee_name_keyword',
          'employee_payroll_id_keyword',
          'tas_program_instance_id_keyword',
          'tas_program_template_field_program_name_keyword',
          'tas_program_instance_field_school_institution_name_keyword',
          // State and date fields for fallback logic
          'tas_application_state_keyword',
          'tas_program_instance_state_keyword',
          'tas_application_created_at_date',
          'tas_program_instance_created_at_date',
        ];

        sampleDocumentFields = [...new Set([...snakeCaseColumns, ...essentialAggregationFields])];

        fallbackCustomColumns.forEach((columnKey, index) => {
          // Column keys are already snake_case from the /fields API.
          // For flattened-container keys (container.leaf), use as-is.
          const existsField = columnKey;
          aggregatedQuery.addNestedAggregation(
            'employees',
            this.aggregateFallbackAggregationName(index),
            {
              filter: {
                exists: {
                  field: existsField,
                },
              },
              aggs: {
                firstHit: {
                  top_hits: {
                    size: 1,
                    _source: {
                      includes: this.selectedSourceColumns([columnKey]),
                    },
                    sort: AGGREGATE_TOP_HIT_SORT,
                  },
                },
              },
            }
          );
        });
      }

      aggregatedQuery.addNestedAggregation('employees', 'sample_document', {
        top_hits: {
          size: 1,
          _source: {
            includes: sampleDocumentFields,
          },
          sort: AGGREGATE_TOP_HIT_SORT,
        },
      });

      const builtQuery = aggregatedQuery.build();

      return {
        query: builtQuery,
        aggs: builtQuery.aggs || {},
      };
    }
  }

  searchData = trackedFunction(this, async () => {
    if (this.args.isReady === false) {
      this.lastSearchRequestKey = undefined;
      this.lastSearchRequestPromise = undefined;
      return undefined as unknown as OpenSearchResponse<
        TasProgramInstance | Record<string, unknown>
      >;
    }

    // Determine model type based on selected dataset
    const dataset = this.selectedDatasetKey;
    const modelType = this.resolveModelType(dataset);

    const rawQuery = this.buildRawOpenSearchQuery();

    const base = {
      'model-type': modelType,
      searchBody: rawQuery.query,
      aggregations: rawQuery.aggs,
    };

    const requestKey = JSON.stringify(base);
    if (this.lastSearchRequestKey === requestKey && this.lastSearchRequestPromise) {
      return this.lastSearchRequestPromise;
    }

    this.lastSearchRequestKey = requestKey;
    const startTime = typeof performance !== 'undefined' ? performance.now() : Date.now();
    // WarpDrive wave 1.5: execute via the shared search builder (replaces
    // `store.adapterFor('search').search`); unwrap `.content`.
    this.lastSearchRequestPromise = this.store
      .request<OpenSearchResponse<TasProgramInstance | Record<string, unknown>>>(
        executeSearchRequest(base)
      )
      .then(({ content }) => {
        const elapsedMs =
          (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startTime;
        if (elapsedMs > 2000) {
          console.warn('[TAS Report] Search request slow', {
            elapsedMs: Math.round(elapsedMs),
            dataset,
            modelType,
            selectedColumns: this.args.selectedColumns?.length ?? 0,
          });
        }
        // BACKEND CONTRACT CANARY:
        // This data provider was simplified to remove the previous client-side
        // `normalizeTasCustomFlattenedContainers` step. It now relies on the
        // backend (tio-api search service) to flatten nested custom-field
        // containers (e.g. `tas_application_fields`,
        // `all_employee_custom_fields_flattened`,
        // `tas_program_instance_custom_fields_flattened`) onto the document
        // root before returning hits. If a contract drift causes those keys
        // to come back as nested arrays again, downstream column rendering
        // will silently show empty cells for the affected fields. This canary
        // logs a single warning per response so regressions surface immediately
        // rather than as silent data loss in reports.
        const typed = content as OpenSearchResponse<TasProgramInstance | Record<string, unknown>>;
        const firstSource = typed?.hits?.hits?.[0]?._source as Record<string, unknown> | undefined;
        if (firstSource) {
          const NESTED_CONTAINER_KEYS = [
            'tas_application_fields',
            'all_employee_custom_fields_flattened',
            'tas_program_instance_custom_fields_flattened',
          ];
          for (const key of NESTED_CONTAINER_KEYS) {
            if (Array.isArray(firstSource[key])) {
              console.warn(
                `[TAS Report] Backend contract drift: "${key}" returned as nested array; expected flattened root keys. Custom fields may render empty until tio-api re-flattens.`
              );
              break;
            }
          }
        }

        // Dispatch onResolvedSource here (in the response settle path) rather
        // than in the resolvedSource getter, so reading the value stays pure.
        // Glimmer disallows tracked writes during render, so always defer to
        // a microtask before invoking the parent's reaction (which typically
        // calls setSelectedColumns).
        this.notifyResolvedSourceIfChanged(typed);

        return content as OpenSearchResponse<TasProgramInstance | Record<string, unknown>>;
      });

    try {
      return await this.lastSearchRequestPromise;
    } catch (error) {
      if (this.lastSearchRequestKey === requestKey) {
        this.lastSearchRequestPromise = undefined;
      }
      throw error;
    }
  });

  downloadCsv = async () => {
    // Note: companyId is optional - global analytics (isGlobalContext=true) doesn't require it
    const rawQuery = this.buildRawOpenSearchQuery();
    const dataset = this.selectedDatasetKey;
    const modelType = this.resolveModelType(dataset);

    const base = {
      'model-type': modelType,
      searchBody: rawQuery.query,
      aggregations: rawQuery.aggs,
      csv_export: true,
      aggregated_mode: !this.args.showIndividualApplications,
    };

    // WarpDrive wave 1.5: download the CSV via the shared raw-fetch helper
    // (replaces `store.adapterFor('search').downloadCsv`). tio-data is
    // owner-free, so the per-app api key is passed from the features service.
    return downloadSearchCsv(base, { apiKey: this.features.ENV?.apiKey ?? '' });
  };

  refresh = () => {
    this.searchData.retry();
  };

  updateQueryParameters = (params: Record<string, unknown>) => {
    // Convert null values to null/undefined to ensure query parameters are removed from URL
    const processedParams = Object.fromEntries(
      Object.entries(params).map(([key, value]) => {
        // Convert null values to null to remove query parameters
        if (value === null || value === undefined) {
          return [key, null];
        }
        return [key, value];
      })
    );

    this.args.updateQueryParameters(processedParams);
  };

  buildChartData = (field: string): ChartData | null => {
    const response = this.searchData.value;
    if (!response) return null;

    const responseBuilder = OpenSearchResponseBuilder.from(response);

    // For aggregated mode, extract data from the employee aggregations
    if (!this.args.showIndividualApplications) {
      // Type assertion is safe: aggregated mode is only used for TAS datasets
      const employeeResults = this.transformProgramInstanceAggregation(
        responseBuilder as OpenSearchResponseBuilder<TasProgramInstance>
      );

      if (field === 'application_state') {
        const stateAggregate: Record<string, number> = {};
        let total = 0;

        employeeResults.forEach((result) => {
          Object.entries(result.applicationStateCounts).forEach(([state, count]) => {
            stateAggregate[state] = (stateAggregate[state] || 0) + count;
            total += count;
          });
        });

        const dataPoints: ChartDataPoint[] = Object.entries(stateAggregate).map(([key, value]) => ({
          category: key,
          value,
          count: value,
          metadata: {
            label: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
          },
        }));

        return { field, dataPoints, total };
      }

      if (field === 'employee_name') {
        const dataPoints: ChartDataPoint[] = employeeResults.map((result) => ({
          category: result.employeeId,
          value: result.totalApplications,
          count: result.totalApplications,
          metadata: {
            label: result.employeeName || `Employee ${result.employeeId}`,
          },
        }));

        const total = dataPoints.reduce((sum, point) => sum + point.value, 0);
        return { field, dataPoints, total };
      }
    }

    // For individual mode, build aggregations from the documents
    const items = responseBuilder.getDocuments() as Array<Record<string, unknown>>;

    if (field === 'application_state') {
      const stateAggregate: Record<string, number> = {};

      items.forEach((item) => {
        const state =
          (item as { tasApplicationStateKeyword?: string }).tasApplicationStateKeyword || 'unknown';
        stateAggregate[state] = (stateAggregate[state] || 0) + 1;
      });

      const dataPoints: ChartDataPoint[] = Object.entries(stateAggregate).map(([key, value]) => ({
        category: key,
        value,
        count: value,
        metadata: {
          label: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
        },
      }));

      return { field, dataPoints, total: items.length };
    }

    return null;
  };

  getAvailableChartFields = () => {
    return [
      { key: 'application_state', label: 'Application State' },
      { key: 'employee_name', label: 'Employee Name' },
      { key: 'employee_payroll_id_keyword', label: 'Payroll ID' },
      { key: 'program_name', label: 'Program Name' },
    ];
  };

  get totalRecords() {
    return this.results.total || 0;
  }

  get executionTime() {
    return this.results.took || 0;
  }

  /**
   * Cache key for the most recent resolvedSource we've notified the parent
   * about. Used to ensure `args.onResolvedSource` fires exactly once per
   * distinct response, preventing render loops when the parent's reaction
   * is itself a tracked write (e.g. setSelectedColumns).
   */
  private lastNotifiedResolvedSourceKey?: string;

  /**
   * Invoked from the searchData trackedFunction's response-settle path.
   * Dedupes by joined contents and defers the parent callback to a
   * microtask so we never call back into a tracked-write reaction inside
   * the same render cycle.
   */
  private notifyResolvedSourceIfChanged(
    response: OpenSearchResponse<TasProgramInstance | Record<string, unknown>> | undefined
  ): void {
    const resolved = response?.fieldMeta?.resolvedSource;
    if (!Array.isArray(resolved)) {
      return;
    }

    const key = resolved.join('\u0000');
    if (key === this.lastNotifiedResolvedSourceKey) {
      return;
    }
    this.lastNotifiedResolvedSourceKey = key;

    const callback = this.args.onResolvedSource;
    if (callback) {
      queueMicrotask(() => callback(resolved));
    }
  }

  /**
   * Canonical _source paths the backend resolved for the most recent
   * response. Returns undefined when the response did not include a
   * _source filter, the API does not yet emit fieldMeta, or no response
   * has arrived yet. Pure read — the parent-callback dispatch happens in
   * the searchData settle path (notifyResolvedSourceIfChanged).
   */
  get resolvedSource(): string[] | undefined {
    const response = this.searchData.value as
      OpenSearchResponse<TasProgramInstance | Record<string, unknown>> | undefined;
    const resolved = response?.fieldMeta?.resolvedSource;
    return Array.isArray(resolved) ? resolved : undefined;
  }

  get programTemplates() {
    return this.args.programTemplates || [];
  }

  get isLoadingProgramTemplates() {
    return this.args.isLoadingProgramTemplates || false;
  }

  get selectedProgramTemplate() {
    return this.args.selectedProgramTemplate;
  }

  get searchError(): Error | undefined {
    const error = this.searchData.error;
    if (!error || (typeof error === 'object' && Object.keys(error).length === 0)) {
      return undefined;
    }
    if (error instanceof Error) {
      return error;
    }
    // Convert generic error object to Error instance
    return new Error(JSON.stringify(error));
  }

  <template>
    {{yield
      (hash
        data=this.results
        isLoading=this.searchData.isLoading
        isDataLoaded=this.searchData.isFinished
        error=this.searchError
        programTemplates=(or this.programTemplates (array))
        isLoadingProgramTemplates=this.isLoadingProgramTemplates
        selectedProgramTemplate=this.selectedProgramTemplate
        downloadCsv=this.downloadCsv
        refresh=this.refresh
        updateQueryParameters=this.updateQueryParameters
        buildChartData=this.buildChartData
        getAvailableChartFields=this.getAvailableChartFields
        totalRecords=this.totalRecords
        executionTime=this.executionTime
        resolvedSource=this.resolvedSource
      )
    }}
  </template>
}
